import { useCallback, useRef, useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { colorBorder, colorSurfaceHover, colorTextSecondary, colorAccent } from "src/design/tokens";
import { BulkCreateRestaurantEntryPayload } from "src/data/admin/types/restaurant";

interface Props {
    entries: BulkCreateRestaurantEntryPayload[] | null;
    onFile: (file: File) => void;
}

const BulkRestaurantDropzone = ({ entries, onFile }: Props) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragging, setDragging] = useState(false);

    const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) onFile(file);
    }, [onFile]);

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) onFile(file);
        e.target.value = "";
    };

    return (
        <Box
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            sx={{
                border: "2px dashed",
                borderColor: dragging ? colorAccent : colorBorder,
                borderRadius: 2,
                p: 5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                backgroundColor: dragging ? colorSurfaceHover : "background.paper",
                transition: "border-color 0.2s, background-color 0.2s",
                userSelect: "none",
            }}
        >
            <UploadFileIcon sx={{ fontSize: 40, color: colorTextSecondary }} />
            <Typography variant="body2" color="text.secondary">
                Drag &amp; drop a CSV file here, or click to browse
            </Typography>
            <Typography variant="caption" color="text.disabled" textAlign="center">
                Columns: name, cityName, quartier, price, phone, address, latitude, longitude, hours,
                bookingDuration, paymentMethods, numberOfSeats, live, servesAlcohol, featured,
                googleRating, hasAnimation, instaTag, bookingEnabled, tags (semicolon-separated)
            </Typography>
            {entries && (
                <Chip
                    label={`${entries.length} restaurant${entries.length === 1 ? "" : "s"} loaded`}
                    color="success"
                    size="small"
                />
            )}
            <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={onFileChange}
            />
        </Box>
    );
};

export default BulkRestaurantDropzone;
